import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import SectionExtractedDataEditor, { pruneForSave } from './SectionExtractedDataEditor';
import {
  HIDDEN_EXTRACTED_KEYS,
  filterPositiveFindings,
  safeNormalizeSectionExtractedData,
  getSchemaForSection,
  applySchemaDefaults,
  deriveSectionDisplayStatus,
  HIDE_SECTION_FINDINGS,
} from '../../constants/complianceSectionSchemas';
import {
  filterHumanReviewFlagsNotInFindings,
  filterDismissedHumanReviewFlags,
  humanReviewFlagText,
  humanReviewDismissPayload,
} from '../../utils/checklistFormatting';
import { DismissReviewButton, buildPageFileMap } from './HumanReviewModal';
import { sectionDefaultFile } from '../common/citations/citationModel';

const FIELD_LABELS = {
  iep_start_date: 'IEP Start Date',
  initial_placement_date: 'Initial Placement',
  iep_annual_review_date: 'IEP Annual Review Date',
  annual_review_date: 'IEP Annual Review Date',
  iep_end_date: 'IEP End Date (if different from annual review)',
  expected_iep_end_date: 'Expected IEP End (364 days from review)',
  previous_schools: 'Previous Schools',
  eligibility_classification: 'Eligibility Classification',
  meeting_dates: 'Meeting Dates',
  dob_conflicts: 'DOB Conflicts Across Documents',
  participation_by_assessment: 'Statewide Assessment',
  lea_school_assessments: 'LEA/School Assessments',
  accommodations_table1: 'Accommodations Table',
  computed_grade: 'Computed Grade (from initial placement)',
  grade_computation_note: 'Grade Computation Note',
  grade_at_initial_placement: 'Grade at Initial Placement',
  validation_results: 'Rule Validation Results',
  transportation_provided: 'Transportation Provided',
  essential_elements_instruction: 'Essential Elements Instruction',
  iep_compliance: 'IEP Compliance Checklist',
  benchmark_objectives: 'Benchmark Objectives',
};

function formatKey(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isEmptyValue(val) {
  if (val === null || val === undefined) return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
}

function StatusBadge({ status }) {
  const cfg = {
    compliant:       { label: 'Compliant',      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /> },
    needs_attention: { label: 'Needs Attention', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /> },
    non_compliant:   { label: 'Non-Compliant',   cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /> },
  };
  const c = cfg[status] || cfg.non_compliant;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${c.cls}`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">{c.icon}</svg>
      {c.label}
    </span>
  );
}

function formatSimpleValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function ObjectCard({ item, index }) {
  const nameField = item.name || item.goal_area || item.service_type;
  const entries = Object.entries(item).filter(([, v]) => !isEmptyValue(v));
  if (entries.length === 0) return null;

  return (
    <div className="rounded-lg bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700/50 p-3 text-sm">
      {nameField && (
        <div className="font-semibold text-gray-900 dark:text-white text-xs mb-2 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-600/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{index + 1}</span>
          {nameField}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {entries.filter(([k]) => k !== 'name' && k !== 'goal_area' && k !== 'service_type').map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-gray-400 dark:text-slate-500">{formatKey(k)}: </span>
            <span className="text-gray-700 dark:text-slate-200">
              {typeof v === 'object' && v !== null ? <ValueRenderer value={v} /> : formatSimpleValue(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ValueRenderer({ value }) {
  if (isEmptyValue(value)) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'string' || typeof value === 'number') return <span>{String(value)}</span>;

  if (Array.isArray(value)) {
    if (value.every(v => typeof v === 'string' || typeof v === 'number')) return <span>{value.join(', ')}</span>;
    return (
      <div className="space-y-2 max-h-[36rem] overflow-y-auto">
        {value.map((item, i) => {
          if (typeof item !== 'object' || item === null) return <div key={i}>{String(item)}</div>;
          return <ObjectCard key={i} item={item} index={i} />;
        })}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => !isEmptyValue(v));
    if (entries.length === 0) return <span className="text-gray-400 dark:text-slate-500">—</span>;
    if (entries.every(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
      return (
        <div className="space-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-2 text-sm">
              <span className="text-gray-500 dark:text-slate-400 font-medium">{formatKey(k)}:</span>
              <span>{formatSimpleValue(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {entries.map(([k, v]) => (
          <div key={k}>
            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{formatKey(k)}</span>
            <div className="mt-0.5 ml-3"><ValueRenderer value={v} /></div>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

const INPUT_CLS = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors';

function EditableList({ items, onChange, placeholder }) {
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(items.map((v, idx) => idx === i ? val : v));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="text" value={item} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} className={INPUT_CLS} />
          <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        Add item
      </button>
    </div>
  );
}

function EditableFindings({ findings, onChange }) {
  const add = () => onChange([...findings, { description: '', passed: true }]);
  const remove = (i) => onChange(findings.filter((_, idx) => idx !== i));
  const update = (i, field, val) => onChange(findings.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  return (
    <div className="space-y-2">
      {findings.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => update(i, 'passed', !f.passed)}
            className={`w-8 h-5 rounded-full flex-shrink-0 flex items-center transition-colors ${f.passed ? 'bg-green-500 justify-end' : 'bg-red-500 justify-start'}`}
          >
            <span className="w-3.5 h-3.5 bg-white rounded-full mx-0.5 shadow-sm" />
          </button>
          <input type="text" value={f.description} onChange={(e) => update(i, 'description', e.target.value)} placeholder="Finding description..." className={INPUT_CLS} />
          <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        Add finding
      </button>
    </div>
  );
}

const WIDE_MODAL_SECTIONS = new Set([
  'evaluation',
  'present_levels',
  'goals',
  'iep_compliance',
  'eligibility',
  'services_lre',
]);

function modalWidthClass(sectionKey) {
  return WIDE_MODAL_SECTIONS.has(sectionKey)
    ? 'max-w-[min(100vw,64rem)] lg:max-w-5xl'
    : 'max-w-2xl';
}

function isMeaningfulServiceRow(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.values(row).some(
    v => v != null && String(v).trim() !== '' && !['null', 'n/a', 'none'].includes(String(v).toLowerCase())
  );
}

function ServicesLreTable({ services }) {
  const meaningful = (services || []).filter(isMeaningfulServiceRow);
  if (meaningful.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 dark:bg-slate-900">
            <th className="px-2.5 py-2 text-left font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Service Type</th>
            <th className="px-2.5 py-2 text-left font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Frequency</th>
            <th className="px-2.5 py-2 text-left font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Duration</th>
            <th className="px-2.5 py-2 text-left font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Environment</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
          {meaningful.map((svc, i) => (
            <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50">
              <td className="px-2.5 py-2 text-gray-700 dark:text-slate-200 font-medium whitespace-nowrap">{svc.service_type || '—'}</td>
              <td className="px-2.5 py-2 text-gray-700 dark:text-slate-200 whitespace-nowrap">{svc.frequency || '—'}</td>
              <td className="px-2.5 py-2 text-gray-700 dark:text-slate-200 whitespace-nowrap">{svc.duration || '—'}</td>
              <td className="px-2.5 py-2 text-gray-700 dark:text-slate-200">{svc.location || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServicesLreExtractedData({ data }) {
  if (!data || typeof data !== 'object') return null;

  const services = data.services || [];
  const meaningfulServices = services.filter(isMeaningfulServiceRow);
  const hasServices = meaningfulServices.length > 0;

  const lreJustification = data.lre_justification;
  const placementType = data.placement_type;
  const hasLre = lreJustification || placementType;

  const knownKeys = new Set([
    'services', 'lre_justification', 'placement_type', 'percentage_general_ed',
  ]);
  const remainingKeys = Object.keys(data).filter(k => !knownKeys.has(k) && !k.startsWith('_') && !isEmptyValue(data[k]));

  return (
    <div className="space-y-5">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Services & LRE Details</h4>

      {/* Services table */}
      <div>
        <h5 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          Services
        </h5>
        {hasServices ? (
          <ServicesLreTable services={services} />
        ) : (
          <div className="rounded-lg border border-dashed border-gray-200 dark:border-slate-700 p-4 text-center">
            <p className="text-xs text-gray-400 dark:text-slate-500">
              No service rows extracted yet. Re-run this section with the IEP PDF selected, or upload a document that includes the Service and Placement grid.
            </p>
          </div>
        )}
      </div>

      {/* LRE / Placement block */}
      {hasLre && (
        <div>
          <h5 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
            LRE &amp; Placement
          </h5>
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden divide-y divide-gray-100 dark:divide-slate-700">
            {placementType && (
              <div className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400 w-36 flex-shrink-0">Placement Type</span>
                <span className="text-sm text-gray-700 dark:text-slate-200">{placementType}</span>
              </div>
            )}
            {lreJustification && (
              <div className="px-3 py-2.5">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">LRE Justification</span>
                <p className="text-sm text-gray-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{lreJustification}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remaining extracted fields */}
      {remainingKeys.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Additional Data</h5>
          <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full min-w-[280px]">
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {remainingKeys.map(key => (
                  <tr key={key}>
                    <td className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-1/3 align-top">{formatKey(key)}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-slate-200"><ValueRenderer value={data[key]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function formatFileDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return ''; }
}

const FILE_STATUS_STYLES = {
  ready:      { label: 'Ready',      dot: 'bg-green-500' },
  processing: { label: 'Processing', dot: 'bg-amber-400 animate-pulse' },
  failed:     { label: 'Failed',     dot: 'bg-red-500' },
};

function trimThinEligibilitySourcePages(sourceMetadata, flags, extracted) {
  if (!sourceMetadata || typeof sourceMetadata !== 'object') return sourceMetadata;
  const ed = extracted && typeof extracted === 'object' ? extracted : {};
  const visibleKeys = Object.keys(ed).filter((k) => k && !k.startsWith('_'));
  const thin = visibleKeys.length === 0
    || (visibleKeys.length === 1 && visibleKeys[0] === 'notice_of_meeting');
  if (!thin) return sourceMetadata;

  const keep = new Set();
  for (const flag of flags || []) {
    const pages = flag?.pages;
    if (!Array.isArray(pages)) {
      const n = Number(flag?.page);
      if (Number.isFinite(n) && n > 0) keep.add(Math.trunc(n));
      continue;
    }
    for (const pg of pages) {
      const n = Number(pg?.page ?? pg?.page_number ?? pg);
      if (Number.isFinite(n) && n > 0) keep.add(Math.trunc(n));
    }
  }

  const filterPages = (pages) => {
    if (!Array.isArray(pages)) return [];
    return pages.filter((pg) => keep.has(Number(pg?.page ?? pg?.page_number ?? pg)));
  };

  const files = Array.isArray(sourceMetadata.files)
    ? sourceMetadata.files.map((f) => (
      !f || typeof f !== 'object'
        ? f
        : { ...f, pages: filterPages(f.pages), page_numbers: filterPages(f.page_numbers) }
    ))
    : sourceMetadata.files;
  return {
    ...sourceMetadata,
    files,
    page_numbers: filterPages(sourceMetadata.page_numbers),
  };
}

function normalizeFlagPages(pages) {
  if (!Array.isArray(pages)) return [];
  const best = new Map();
  for (const raw of pages) {
    const pageNum = Number(raw?.page ?? raw?.page_number ?? raw);
    if (!Number.isFinite(pageNum) || pageNum < 1) continue;
    const page = Math.trunc(pageNum);
    const score = Number(raw?.score);
    const row = {
      page,
      score: Number.isFinite(score) ? score : 0,
      student_file_id: raw?.student_file_id ?? raw?.file_id ?? null,
      file_name: raw?.file_name || raw?.source_file || null,
    };
    const prev = best.get(page);
    if (!prev || row.score > prev.score) best.set(page, row);
  }
  return Array.from(best.values())
    .sort((a, b) => b.score - a.score || a.page - b.page)
    .slice(0, 8);
}

function FlagPageLinks({ pages, onOpenFileAtPage, pageFileMap }) {
  const rows = normalizeFlagPages(pages);
  if (!rows.length) return null;
  return (
    <span className="ml-1 inline-flex flex-wrap items-center gap-1 align-middle">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700/70 dark:text-amber-300/60 mr-0.5">Source:</span>
      {rows.map((pg) => {
        const mapped = pageFileMap?.get(pg.page);
        const def = pageFileMap?.get('__default__');
        const fileId = pg.student_file_id ?? mapped?.fileId ?? def?.fileId ?? null;
        const fileName = pg.file_name || mapped?.fileName || def?.fileName || null;
        const label = `p. ${pg.page}`;
        if (fileId != null && onOpenFileAtPage) {
          return (
            <button
              key={`${fileId}-${pg.page}`}
              type="button"
              onClick={() => onOpenFileAtPage(fileId, pg.page, fileName)}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
              title={fileName ? `${fileName} — p. ${pg.page}` : label}
            >
              {label}
            </button>
          );
        }
        return (
          <span
            key={`p-${pg.page}`}
            className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-xs font-medium"
          >
            {label}
          </span>
        );
      })}
    </span>
  );
}

export default function SectionDetailModal({
  open, section, sectionKey, label, subtitle, isRerunning, onClose, onRerun,
  onSave, onDismissFinding, studentFiles = [], onCrossDocRerun, onOpenFileAtPage,
  variant = 'modal', cycleLabel, onOpenSource = null,
}) {
  const isPage = variant === 'page';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [crossDocRunning, setCrossDocRunning] = useState(false);
  const rerunning = isRerunning || crossDocRunning;

  const [editStatus, setEditStatus] = useState('compliant');
  const [editFindings, setEditFindings] = useState([]);
  const [editRecommendations, setEditRecommendations] = useState([]);
  const [editMissingData, setEditMissingData] = useState([]);
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [editExtractedData, setEditExtractedData] = useState({});
  const [editNotes, setEditNotes] = useState('');
  const [editError, setEditError] = useState('');
  const [humanReviewOpen, setHumanReviewOpen] = useState(false);
  const docPickerRef = useRef(null);

  useEffect(() => {
    setHumanReviewOpen(false);
  }, [sectionKey]);

  useEffect(() => {
    if (!docDropdownOpen) return undefined;
    const handleOutside = (e) => {
      if (docPickerRef.current && !docPickerRef.current.contains(e.target)) {
        setDocDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [docDropdownOpen]);

  const startEditing = useCallback(() => {
    if (!section) return;
    setEditStatus(section.status || 'compliant');
    setEditFindings((section.findings || []).map(f => ({ ...f })));
    setEditRecommendations((section.recommendations || []).map(r => typeof r === 'string' ? r : r.description || ''));
    setEditMissingData((section.missing_data || []).map(m => typeof m === 'string' ? m : m.description || ''));
    setEditSuggestions((section.suggestions || []).map(s => typeof s === 'string' ? s : s.description || ''));
    try {
      setEditExtractedData(
        applySchemaDefaults(sectionKey, safeNormalizeSectionExtractedData(sectionKey, section)),
      );
    } catch { setEditExtractedData(applySchemaDefaults(sectionKey, {})); }
    setEditNotes('');
    setEditError('');
    setEditing(true);
  }, [section, sectionKey]);

  const cancelEditing = () => { setEditing(false); setEditError(''); };

  const handleSave = async () => {
    setSaving(true);
    setEditError('');

    const schema = getSchemaForSection(sectionKey);
    const payload = {
      status: editStatus,
      findings: editFindings.filter(f => f.description.trim()),
      recommendations: editRecommendations.filter(r => r.trim()),
      missing_data: editMissingData.filter(m => m.trim()),
      suggestions: editSuggestions.filter(s => s.trim()),
      extracted_data: schema ? pruneForSave(editExtractedData, schema, sectionKey) : editExtractedData,
    };
    if (editNotes.trim()) payload.notes = editNotes.trim();

    try {
      await onSave(sectionKey, payload);
      setEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const readyFiles = studentFiles.filter(f => !f.status || f.status === 'ready');

  const filteredDocFiles = useMemo(() => {
    const q = docSearchQuery.trim().toLowerCase();
    if (!q) return studentFiles;
    return studentFiles.filter((f) => {
      const name = (f.file_name || '').toLowerCase();
      const type = (f.file_type || '').toLowerCase();
      const id = String(f.id ?? '');
      return name.includes(q) || type.includes(q) || id.includes(q);
    });
  }, [studentFiles, docSearchQuery]);

  const filteredReadyFiles = filteredDocFiles.filter(f => !f.status || f.status === 'ready');

  const toggleDocSelect = (fileId) => {
    setSelectedDocIds(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    const readyIds = filteredReadyFiles.map(f => f.id);
    const allSelected = readyIds.length > 0 && readyIds.every((id) => selectedDocIds.includes(id));
    setSelectedDocIds(allSelected ? [] : readyIds);
  };

  const handleUnifiedRerun = async () => {
    if (selectedDocIds.length > 0 && onCrossDocRerun) {
      setCrossDocRunning(true);
      try {
        await onCrossDocRerun(sectionKey, selectedDocIds);
        setDocDropdownOpen(false);
        setSelectedDocIds([]);
      } catch { /* parent handles alert */ }
      finally { setCrossDocRunning(false); }
    } else {
      try { await onRerun(sectionKey); } catch { /* parent handles state */ }
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { if (editing) cancelEditing(); else onClose(); }
  }, [editing, onClose]);

  useEffect(() => {
    if (isPage) return undefined;
    if (open) { document.addEventListener('keydown', handleKeyDown); document.body.style.overflow = 'hidden'; }
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [open, handleKeyDown, isPage]);

  useEffect(() => { if (!open) { setEditing(false); setDocDropdownOpen(false); setDocSearchQuery(''); setSelectedDocIds([]); } }, [open]);

  const pageFileMap = useMemo(() => buildPageFileMap(section?.source_metadata), [section]);

  if (!open || !section) return null;

  const {
    findings: rawFindings = [], extracted_data, recommendations = [], missing_data = [],
    suggestions = [], source_metadata, user_overrides, dismissed_findings = [],
    human_review_flags = [],
  } = section;
  const findings = HIDE_SECTION_FINDINGS.has(sectionKey) ? [] : filterPositiveFindings(rawFindings);
  const dismissedSet = new Set(dismissed_findings || []);
  const uniqueHumanReviewFlags = filterDismissedHumanReviewFlags(
    filterHumanReviewFlagsNotInFindings(human_review_flags, findings),
    dismissed_findings,
  );
  const displayStatus = deriveSectionDisplayStatus(sectionKey, section);
  const displaySourceMetadata = sectionKey === 'eligibility'
    ? trimThinEligibilitySourcePages(source_metadata, uniqueHumanReviewFlags, extracted_data)
    : source_metadata;
  const isServicesLre = sectionKey === 'services_lre';
  const extractedEntries = extracted_data && typeof extracted_data === 'object'
    ? Object.entries(extracted_data).filter(([k, v]) => !HIDDEN_EXTRACTED_KEYS.has(k) && !isEmptyValue(v))
    : [];

  const cardClass = isPage
    ? 'relative bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl w-full flex flex-col max-h-[calc(100vh-7.5rem)] lg:max-h-[calc(100vh-4rem)]'
    : `relative bg-white dark:bg-slate-800 shadow-2xl border border-gray-200 dark:border-slate-700 rounded-none sm:rounded-2xl w-full ${modalWidthClass(sectionKey)} sm:mx-4 max-h-[100vh] sm:max-h-[95vh] flex flex-col`;

  const headerStickyClass = isPage
    ? 'flex-shrink-0 z-20 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700'
    : 'flex-shrink-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700';

  const headerActionBtn = 'inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50';

  const showDocumentPicker = !editing && onCrossDocRerun && studentFiles.length > 0;
  const rerunButtonLabel = selectedDocIds.length > 0 ? ` (${selectedDocIds.length})` : '';

  const renderActionBox = () => (
    <div ref={docPickerRef} className="relative flex items-center h-11 w-full max-w-xl rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 overflow-visible shadow-sm">
      {showDocumentPicker ? (
        <div className="relative h-full flex-1 min-w-0">
          <div className="h-full flex items-center gap-2 px-3">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              value={docSearchQuery}
              onChange={(e) => {
                setDocSearchQuery(e.target.value);
                if (!docDropdownOpen) setDocDropdownOpen(true);
              }}
              onFocus={() => setDocDropdownOpen(true)}
              onClick={() => setDocDropdownOpen(true)}
              placeholder={selectedDocIds.length > 0 ? `${selectedDocIds.length} file${selectedDocIds.length > 1 ? 's' : ''} selected` : 'Search files...'}
              className="h-full w-full bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none"
            />
            {selectedDocIds.length > 0 && (
              <span className="flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-md bg-indigo-100 dark:bg-indigo-600/25 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold flex items-center justify-center">
                {selectedDocIds.length}
              </span>
            )}
          </div>
          {docDropdownOpen && (
            <div className="absolute left-0 right-0 top-full mt-1.5 z-30 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Student files</span>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-xs text-indigo-600 dark:text-indigo-400 font-medium whitespace-nowrap"
                >
                  {filteredReadyFiles.length > 0 && filteredReadyFiles.every((f) => selectedDocIds.includes(f.id))
                    ? 'Clear'
                    : 'Select all'}
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                {filteredDocFiles.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-gray-500 dark:text-slate-400 text-center">No files match</p>
                ) : filteredDocFiles.map(f => {
                  const isReady = !f.status || f.status === 'ready';
                  const isSelected = selectedDocIds.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      disabled={!isReady}
                      onClick={() => toggleDocSelect(f.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${
                        isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : isReady ? 'hover:bg-gray-50 dark:hover:bg-slate-700/50' : 'opacity-40 cursor-not-allowed'
                      }`}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 dark:border-slate-600'
                      }`}>
                        {isSelected && <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{f.file_name}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-w-0 px-3 text-sm text-gray-400 dark:text-slate-500">No files</div>
      )}
      <button
        type="button"
        onClick={handleUnifiedRerun}
        disabled={rerunning}
        title="Re-run section"
        className="h-full inline-flex items-center justify-center gap-1.5 px-4 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50 transition-colors border-l border-gray-200 dark:border-slate-600 flex-shrink-0"
      >
        {rerunning ? (
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        )}
        <span>{rerunning ? 'Running' : `Re-run${rerunButtonLabel}`}</span>
      </button>
    </div>
  );

  const card = (
    <div className={cardClass}>

        {/* Header — sticky on section page; save/cancel live here in edit mode */}
        <div className={headerStickyClass}>
        <div className="flex items-center px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0 gap-3 overflow-visible">
          <div className="min-w-0 flex-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white break-words">{label}</h3>
                {isPage && cycleLabel && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{cycleLabel}</span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
              {isPage && !editing && section.issues_count != null && (
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1 sm:hidden">
                  {section.issues_count} issue{section.issues_count !== 1 ? 's' : ''} found
                </p>
              )}
            </div>
          </div>
          {!editing && (
            <div className="hidden sm:flex flex-1 justify-center min-w-0 px-2">
              {renderActionBox()}
            </div>
          )}
          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
            {!editing && (
              <div className="sm:hidden">
                {renderActionBox()}
              </div>
            )}
            {!editing && uniqueHumanReviewFlags.length > 0 && (
              <button
                type="button"
                onClick={() => setHumanReviewOpen((open) => !open)}
                className={`${headerActionBtn} bg-amber-500 text-white shadow-sm hover:bg-amber-600`}
                title="Show human review items"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="hidden sm:inline">Human review</span>
                <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-white/20 text-[11px] font-bold tabular-nums">
                  {uniqueHumanReviewFlags.length}
                </span>
              </button>
            )}
            {!editing && <StatusBadge status={displayStatus} />}
            {!editing && (
              <button
                type="button"
                onClick={startEditing}
                title="Edit section"
                className={`${headerActionBtn} border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}
            {!editing && (
              <button
                type="button"
                onClick={onClose}
                title={isPage ? 'Back' : 'Close'}
                className={`${headerActionBtn} border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                <span className="hidden sm:inline">{isPage ? 'Back' : 'Close'}</span>
              </button>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={saving}
                  title="Cancel editing"
                  className={`${headerActionBtn} border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="hidden sm:inline">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  title="Save changes"
                  className={`${headerActionBtn} bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm`}
                >
                  {saving ? (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )}
                  <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>
        </div>

        {/* Body */}
        <div className={`${isPage ? 'flex-1 min-h-0 overflow-y-auto' : 'flex-1 overflow-y-auto'} p-4 sm:p-6 space-y-5 sm:space-y-6`}>
          {editing ? (
            <>
              {editError && <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">{editError}</div>}

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Status</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className={INPUT_CLS}>
                  <option value="compliant">Compliant</option>
                  <option value="needs_attention">Needs Attention</option>
                  <option value="non_compliant">Non-Compliant</option>
                </select>
              </div>

              {!HIDE_SECTION_FINDINGS.has(sectionKey) && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Findings</label>
                    <EditableFindings findings={editFindings} onChange={setEditFindings} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Recommendations</label>
                    <EditableList items={editRecommendations} onChange={setEditRecommendations} placeholder="Recommendation..." />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Missing Data</label>
                    <EditableList items={editMissingData} onChange={setEditMissingData} placeholder="Missing data item..." />
                  </div>
                </>
              )}

              {/* Suggestions */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Suggestions</label>
                <EditableList items={editSuggestions} onChange={setEditSuggestions} placeholder="Suggestion..." />
              </div>

              {/* Extracted Data — schema-driven per section */}
              <SectionExtractedDataEditor
                sectionKey={sectionKey}
                data={editExtractedData}
                onChange={setEditExtractedData}
                onOpenFileAtPage={onOpenFileAtPage}
                onOpenSource={onOpenSource}
                sourceFileId={sectionDefaultFile(displaySourceMetadata).id}
                sourceFileName={sectionDefaultFile(displaySourceMetadata).name}
              />

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Edit Notes</label>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2} placeholder="Reason for this edit..." className={`${INPUT_CLS} resize-none`} />
              </div>
            </>
          ) : (
            <>
              {humanReviewOpen && uniqueHumanReviewFlags.length > 0 && (
                <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Human review suggested</h4>
                    <button
                      type="button"
                      onClick={() => setHumanReviewOpen(false)}
                      className="p-1 rounded-md text-amber-800/70 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
                      title="Close human review"
                      aria-label="Close human review"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <ul className="space-y-2 text-sm text-amber-900 dark:text-amber-200/90">
                    {uniqueHumanReviewFlags.map((flag, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <span>{humanReviewFlagText(flag)}</span>
                          <FlagPageLinks pages={flag?.pages} onOpenFileAtPage={onOpenFileAtPage} pageFileMap={pageFileMap} />
                        </div>
                        {onDismissFinding && (
                          <DismissReviewButton
                            onClick={() => {
                              const payload = humanReviewDismissPayload(flag);
                              if (payload) onDismissFinding(sectionKey, payload);
                            }}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Findings */}
              {findings.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Findings</h4>
                  <ul className="space-y-2">
                    {findings.map((f, i) => {
                      const isDismissed = dismissedSet.has(f.description) || f.dismissed;
                      const showFail = f.passed === false && !isDismissed;
                      return (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        {f.passed === true || isDismissed ? (
                          <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        ) : (
                          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        )}
                        <span className={`flex-1 break-words ${showFail ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-slate-300'}`}>
                          {f.description}
                          {isDismissed && <span className="ml-1 text-xs text-gray-400 dark:text-slate-500 font-normal">(dismissed)</span>}
                        </span>
                        {showFail && onDismissFinding && (
                          <button
                            type="button"
                            onClick={() => {
                              const payload = f.validator_code === 'EXAMINER_MISSING'
                                ? { dismiss_validator_code: 'EXAMINER_MISSING' }
                                : f.description;
                              onDismissFinding(sectionKey, payload);
                            }}
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0"
                          >
                            Dismiss
                          </button>
                        )}
                      </li>
                    );})}
                  </ul>
                </div>
              )}

              {/* Extracted Data — section-specific or schema-driven */}
              {isServicesLre && extracted_data ? (
                <ServicesLreExtractedData data={extracted_data} />
              ) : getSchemaForSection(sectionKey) ? (
                <div>
                  {sectionKey !== 'present_levels' && sectionKey !== 'goals' && (
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Extracted Data</h4>
                  )}
                  <SectionExtractedDataEditor
                    sectionKey={sectionKey}
                    data={safeNormalizeSectionExtractedData(sectionKey, section)}
                    onChange={() => {}}
                    readOnly
                    onOpenFileAtPage={onOpenFileAtPage}
                onOpenSource={onOpenSource}
                    sourceFileId={sectionDefaultFile(displaySourceMetadata).id}
                    sourceFileName={sectionDefaultFile(displaySourceMetadata).name}
                  />
                </div>
              ) : extractedEntries.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Extracted Data</h4>
                  <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-x-auto">
                    <table className="w-full min-w-[320px]">
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                        {extractedEntries.map(([key, val]) => (
                          <tr key={key}>
                            <td className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-1/3 align-top break-words">{formatKey(key)}</td>
                            <td className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-gray-700 dark:text-slate-200 break-words"><ValueRenderer value={val} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {recommendations.length > 0 && !HIDE_SECTION_FINDINGS.has(sectionKey) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recommendations</h4>
                  <ul className="space-y-2">
                    {recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-amber-700 dark:text-amber-400">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                        {typeof r === 'string' ? r : r.description || JSON.stringify(r)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Missing Data */}
              {missing_data.length > 0 && !HIDE_SECTION_FINDINGS.has(sectionKey) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Missing Data</h4>
                  <ul className="space-y-2">
                    {missing_data.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-red-600 dark:text-red-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                        {typeof item === 'string' ? item : item.description || JSON.stringify(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {suggestions.length > 0 && !HIDE_SECTION_FINDINGS.has(sectionKey) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Suggestions</h4>
                  <ul className="space-y-2">
                    {suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-blue-700 dark:text-blue-400">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                        {typeof s === 'string' ? s : s.description || JSON.stringify(s)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Source Metadata */}
              {displaySourceMetadata && (displaySourceMetadata.files?.length > 0 || displaySourceMetadata.source_file) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Source Information</h4>
                  <div className="rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-4 space-y-3 text-sm">
                    {displaySourceMetadata.files?.length > 0 ? (
                      <div className="space-y-2.5">
                        {displaySourceMetadata.files.map((f, i) => {
                          const fileId = f.student_file_id || f.file_id;
                          const pages = f.pages || [];
                          const pageNums = f.page_numbers || [];
                          return (
                            <div key={i} className="flex items-start gap-2.5 pl-1 border-l-2 border-indigo-200 dark:border-indigo-700">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm">📄</span>
                                  {fileId && onOpenFileAtPage ? (
                                    <button
                                      type="button"
                                      onClick={() => onOpenFileAtPage(fileId, null, f.file_name)}
                                      className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline break-all text-left"
                                    >
                                      {f.file_name}
                                    </button>
                                  ) : (
                                    <span className="font-medium text-gray-800 dark:text-slate-200 break-all">{f.file_name}</span>
                                  )}
                                  {f.content_types?.length > 0 && f.content_types.map((ct, ci) => (
                                    <span key={ci} className="px-1.5 py-0 rounded bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-[10px] font-medium">{ct}</span>
                                  ))}
                                </div>
                                {(pages.length > 0 || pageNums.length > 0) && (
                                  <div className="flex items-center gap-1 mt-1 ml-6 flex-wrap">
                                    <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-semibold mr-0.5">Pages:</span>
                                    {pages.length > 0 ? pages.map((pg, pi) => {
                                      const pgFileId = pg.student_file_id || fileId;
                                      const pgPage = pg.page;
                                      return (
                                        <button
                                          key={pi}
                                          type="button"
                                          onClick={() => onOpenFileAtPage && onOpenFileAtPage(pgFileId, pgPage, f.file_name)}
                                          className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
                                          title={`${f.file_name} — p. ${pgPage}`}
                                        >
                                          p. {pgPage}
                                        </button>
                                      );
                                    }) : pageNums.map((pn, pi) => (
                                      <button
                                        key={pi}
                                        type="button"
                                        onClick={() => fileId && onOpenFileAtPage && onOpenFileAtPage(fileId, pn, f.file_name)}
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                                          fileId && onOpenFileAtPage
                                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800/40 cursor-pointer'
                                            : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                        }`}
                                        title={`${f.file_name} — p. ${pn}`}
                                        disabled={!fileId || !onOpenFileAtPage}
                                      >
                                        p. {pn}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 dark:text-slate-400 font-medium w-28 flex-shrink-0">Source File:</span>
                          <span className="text-gray-700 dark:text-slate-200">{displaySourceMetadata.source_file}</span>
                        </div>
                        {displaySourceMetadata.page_numbers?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 dark:text-slate-400 font-medium w-28 flex-shrink-0">Pages:</span>
                            <span className="text-gray-700 dark:text-slate-200">{displaySourceMetadata.page_numbers.join(', ')}</span>
                          </div>
                        )}
                        {displaySourceMetadata.content_types?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 dark:text-slate-400 font-medium w-28 flex-shrink-0">Content Types:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {displaySourceMetadata.content_types.map((t, ti) => (
                                <span key={ti} className="px-2 py-0.5 rounded-full bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {displaySourceMetadata.generated_at && (
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-slate-700">
                        <span className="text-gray-400 dark:text-slate-500 text-xs">Generated:</span>
                        <span className="text-gray-500 dark:text-slate-400 text-xs">
                          {new Date(displaySourceMetadata.generated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User overrides note */}
              {user_overrides && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-xl px-4 py-2.5">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Last edited{user_overrides.edited_by ? ` by ${user_overrides.edited_by}` : ''}{user_overrides.edited_at ? ` on ${new Date(user_overrides.edited_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                  {user_overrides.notes && <span className="ml-1">— {user_overrides.notes}</span>}
                </div>
              )}

              {/* Empty state */}
              {findings.length === 0 && extractedEntries.length === 0 && !isServicesLre && recommendations.length === 0 && missing_data.length === 0 && suggestions.length === 0 && (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                  </div>
                  <p className="text-gray-500 dark:text-slate-400 text-sm">No detailed data available for this section yet.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — issue count only (actions & documents are in the header) */}
        {!editing && isPage && section.issues_count != null && (
        <div className="border-t border-gray-100 dark:border-slate-700 flex-shrink-0 px-4 sm:px-6 py-2.5 hidden sm:block">
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {section.issues_count} issue{section.issues_count !== 1 ? 's' : ''} found
          </p>
        </div>
        )}
    </div>
  );

  if (isPage) return card;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!editing) onClose(); }} />
      {card}
    </div>
  );
}
